import * as React from 'react';

import {Button} from 'shells/button/button';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';

import type {
    MeetingFragment as Meeting,
    MeetingValidityIssueFragment as MeetingValidityIssue,
} from 'nutshell-graphql-types';

import type {ConferencingModalType} from '../location/conferencing-modals/conferencing-modal-wrapper';

import {MEETING_VALIDITY_ISSUES, GENERIC_HOST_NAME} from './consts';

export const getHostsForMeeting = (meeting: Meeting) => {
    if (meeting.meetingType === 'ROUND_ROBIN') {
        return meeting.hosts;
    }

    return [meeting.creator, ...meeting.cohosts];
};

// Currently not terribly necessary but once zoom and other issues are added, this will be useful
export const getMultipleIssuesText = (issues?: MeetingValidityIssue[]) => {
    // If they've been removed, we don't really need to show other issues I think
    if (
        issues &&
        // @ts-expect-error
        issues.find((issue) => issue.issueType === MEETING_VALIDITY_ISSUES.USER_REMOVED)
    ) {
        // @ts-expect-error
        return MEETING_VALIDITY_ISSUES.USER_REMOVED.text;
    }
    if (issues) {
        // add all issues, special case for last since we don't need a trailing "and"
        let issueText = issues
            .map((item) => {
                // @ts-expect-error
                return MEETING_VALIDITY_ISSUES[item.issueType].name;
            })
            .join(' and ');
        issueText += ' are not connected.';

        return issueText;
    }

    return '';
};

export const getCustomizedIssueDescription = (
    issue: MeetingValidityIssue,
    userName: string,
    isCurrentUser: boolean,
    openCalendaModal: () => void,
    openConferencingModal: (type: ConferencingModalType) => void
): React.ReactNode | null | undefined => {
    switch (issue.issueType) {
        case 'CALENDAR_DISCONNECTED':
            return (
                <div className='pad-16'>
                    {isCurrentUser ? (
                        <StackLegacy spacing={8}>
                            <div>Your calendar needs reconnecting</div>
                            <Button onClick={openCalendaModal} variant='primary' isFullWidth={true}>
                                Connect calendar
                            </Button>
                        </StackLegacy>
                    ) : (
                        // @ts-expect-error
                        MEETING_VALIDITY_ISSUES[issue.issueType].description.replace(
                            GENERIC_HOST_NAME,
                            userName
                        )
                    )}
                </div>
            );
        case 'ZOOM_NOT_ENABLED':
            return (
                <div className='pad-16'>
                    {isCurrentUser ? (
                        <StackLegacy spacing={8}>
                            <div>Your Zoom account needs reconnecting</div>
                            <Button
                                onClick={() => openConferencingModal('ZOOM')}
                                variant='primary'
                                isFullWidth={true}
                            >
                                Connect Zoom
                            </Button>
                        </StackLegacy>
                    ) : (
                        // @ts-expect-error
                        MEETING_VALIDITY_ISSUES[issue.issueType].description.replace(
                            GENERIC_HOST_NAME,
                            userName
                        )
                    )}
                </div>
            );
        case 'O365_GOOGLE_MEET':
            return (
                <div className='pad-16'>
                    {isCurrentUser ? (
                        <StackLegacy spacing={8}>
                            <div>Your Google account needs reconnecting</div>
                            <Button
                                onClick={() => openConferencingModal('MEET')}
                                variant='primary'
                                isFullWidth={true}
                            >
                                Connect Meet
                            </Button>
                        </StackLegacy>
                    ) : (
                        // @ts-expect-error
                        MEETING_VALIDITY_ISSUES[issue.issueType].description.replace(
                            GENERIC_HOST_NAME,
                            userName
                        )
                    )}
                </div>
            );
        case 'TEAMS_NOT_ENABLED':
            return (
                <div className='pad-16'>
                    {isCurrentUser ? (
                        <StackLegacy spacing={8}>
                            <div>Your Teams account needs reconnecting</div>
                            <Button
                                onClick={() => openConferencingModal('TEAMS')}
                                variant='primary'
                                isFullWidth={true}
                            >
                                Connect Teams
                            </Button>
                        </StackLegacy>
                    ) : (
                        // @ts-expect-error
                        MEETING_VALIDITY_ISSUES[issue.issueType].description.replace(
                            GENERIC_HOST_NAME,
                            userName
                        )
                    )}
                </div>
            );
        case 'USER_REMOVED':
            return (
                <div className='pad-16'>
                    {/* @ts-expect-error */}
                    {MEETING_VALIDITY_ISSUES[issue.issueType].description.replace(
                        GENERIC_HOST_NAME,
                        userName
                    )}
                </div>
            );
    }
};

export const getMultipleCustomizedIssueDescriptions = (
    issues: MeetingValidityIssue[],
    userName: string,
    isCurrentUser: boolean,
    openSyncModal: () => void,
    openConferencingModal: (type: ConferencingModalType) => void
): React.ReactNode | null | undefined => {
    // If current user, we want the popover with the CTA button and only for one error at a time, so we use the single issue description
    if (isCurrentUser) {
        return getCustomizedIssueDescription(
            // @ts-expect-error
            issues[0],
            userName,
            isCurrentUser,
            openSyncModal,
            openConferencingModal
        );
    }

    let text = `${userName}'s `;
    // add all issues, special case for last since we don't need a trailing "and"
    text += issues
        .map((item) => {
            // @ts-expect-error
            return MEETING_VALIDITY_ISSUES[item.issueType].name;
        })
        .join(' and ');
    text += ' need attention. Have them visit the meeting to repair the connections.';

    return <div className='pad-16'>{text}</div>;
};
